import { Link } from 'react-router-dom';
import type { GsEvent } from '@/lib/types';
import { fallbackHero } from '@/lib/placeholders';
import { formatEventDate } from '@/lib/format';
import DemoBadge from '@/components/feature/DemoBadge';

export default function TournamentCard({ event }: { event: GsEvent }) {
  const confirmed = event.status.toLowerCase().includes('confirmed');

  return (
    <Link
      to={`/events/${event.slug}`}
      className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-foreground-700 transition-colors cursor-pointer"
    >
      <div className="relative w-full h-48 md:h-52 overflow-hidden">
        <img
          src={event.hero_image || fallbackHero}
          alt={event.name}
          title={`${event.name}, ${event.city}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute top-3 left-3 bg-foreground-950 text-background-50 rounded-md px-3 py-2 text-center">
          <p className="font-label uppercase text-[10px] tracking-widest text-accent-400">{event.weekday}</p>
          <p className="font-heading font-extrabold text-2xl leading-none">{event.day_label}</p>
          <p className="font-label uppercase text-[10px]">{event.month_label} {event.year_label}</p>
        </div>
        {event.is_demo && <div className="absolute top-3 right-3"><DemoBadge label="Sample" /></div>}
      </div>

      <div className="flex flex-col flex-1 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-secondary-100 text-secondary-900 font-label font-semibold uppercase text-[11px] tracking-wide px-2.5 py-1 rounded-sm">{event.event_type}</span>
          <span className={`font-label font-semibold uppercase text-[11px] tracking-wide px-2.5 py-1 rounded-sm ${confirmed ? 'bg-primary-500 text-background-50' : 'bg-accent-100 text-accent-900 border border-accent-300'}`}>{event.status}</span>
        </div>

        <h3 className="mt-3 font-heading font-bold text-xl md:text-2xl leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">{event.name}</h3>

        <p className="mt-2 text-sm text-foreground-700 flex items-center gap-1.5">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
          {event.course}, {event.city}{event.county ? ` \u00b7 ${event.county} County` : ''}
        </p>

        <div className="mt-auto pt-4 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-foreground-600 flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
            {formatEventDate(event.event_date) || `${event.month_label} ${event.day_label}`}
          </span>
          {event.entry_fee && <span className="font-label font-semibold uppercase text-xs tracking-wide text-primary-600">{event.entry_fee}</span>}
        </div>
      </div>
    </Link>
  );
}